import React from 'react';
import { connect, DispatchProp } from 'react-redux';

import { DistanceNumber, SignedNumber } from '@dndbeyond/character-common-components/es';
import { AdvantageIcon, SignedNumberDice } from '@dndbeyond/character-components/es';
import {
    CharacterPreferences,
    SpeedInfo,
    RuleData,
    RuleDataUtils,
    rulesEngineSelectors,
    CharacterTheme,
} from '@dndbeyond/character-rules-engine/es';
import { RollType, DiceTools, IRollContext } from '@dndbeyond/dice';
import { GameLogContext } from '@dndbeyond/game-log-components';

import { sidebarActions } from '../../../../Shared/actions';
import { appEnvSelectors, characterRollContextSelectors } from '../../../../Shared/selectors';
import { PaneComponentEnum } from '../../../../Shared/utils';
import { SheetAppState } from '../../../typings';

interface Props extends DispatchProp {
    armorClass: number;
    speeds: SpeedInfo;
    proficiencyBonus: number;
    initiative: number;
    hasInitiativeAdvantage: boolean;
    preferences: CharacterPreferences;
    ruleData: RuleData;
    diceEnabled: boolean;
    theme: CharacterTheme;
    characterRollContext: IRollContext;
}
class CombatMobile extends React.PureComponent<Props> {
    static defaultProps = {
        diceEnabled: false,
    };

    handleProficiencyBonusClick = (evt: React.MouseEvent): void => {
        const { dispatch } = this.props;
        evt.stopPropagation();
        evt.nativeEvent.stopImmediatePropagation();
        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.PROFICIENCY_BONUS));
    };

    handleSpeedsClick = (evt: React.MouseEvent): void => {
        const { dispatch } = this.props;
        evt.stopPropagation();
        evt.nativeEvent.stopImmediatePropagation();
        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.SPEED_MANAGE));
    };

    handleDefensesSummaryClick = (evt: React.MouseEvent): void => {
        const { dispatch } = this.props;
        evt.stopPropagation();
        evt.nativeEvent.stopImmediatePropagation();
        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.DEFENSE_MANAGE));
    };

    handleConditionSummaryClick = (evt: React.MouseEvent): void => {
        const { dispatch } = this.props;
        evt.stopPropagation();
        evt.nativeEvent.stopImmediatePropagation();
        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.CONDITION_MANAGE));
    };

    handleArmorClassClick = (evt: React.MouseEvent): void => {
        const { dispatch } = this.props;
        evt.stopPropagation();
        evt.nativeEvent.stopImmediatePropagation();
        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.ARMOR_MANAGE));
    };

    handleInitiativeClick = (evt: React.MouseEvent): void => {
        const { dispatch } = this.props;
        evt.stopPropagation();
        evt.nativeEvent.stopImmediatePropagation();
        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.INITIATIVE));
    };

    renderProficiency = (): React.ReactNode => {
        const { proficiencyBonus } = this.props;

        return (
            <div
                className="ct-combat-mobile__extra ct-combat-mobile__extra--proficiency"
                onClick={this.handleProficiencyBonusClick}
            >
                <div className="ct-combat-mobile__extra-heading">
                    <div className="ct-combat-mobile__extra-label">Proficiency</div>
                </div>
                <div className="ct-combat-mobile__extra-value">
                    <SignedNumber number={proficiencyBonus} size={'large'} />
                </div>
                <div className="ct-combat-mobile__extra-footer">
                    <div className="ct-combat-mobile__extra-label">Bonus</div>
                </div>
            </div>
        );
    };

    renderSpeed = (): React.ReactNode => {
        const { preferences, speeds, ruleData, theme } = this.props;

        const displaySpeedValue: number = speeds[RuleDataUtils.getSpeedMovementKeyById(preferences.primaryMovement)];
        const displaySpeedLabel = RuleDataUtils.getMovementDescription(preferences.primaryMovement, ruleData);

        return (
            <div className="ct-combat-mobile__extra  ct-combat-mobile__extra--speed" onClick={this.handleSpeedsClick}>
                <div className="ct-combat-mobile__extra-heading">
                    <div className="ct-combat-mobile__extra-subheading">{displaySpeedLabel}</div>
                </div>
                <div className="ct-combat-mobile__extra-value">
                    <DistanceNumber number={displaySpeedValue} size={'large'} isDarkMode={theme.isDarkMode} />
                </div>
                <div className="ct-combat-mobile__extra-footer">
                    <div className="ct-combat-mobile__extra-label">Speed</div>
                </div>
            </div>
        );
    };

    renderInitiative = (): React.ReactNode => {
        const { initiative, hasInitiativeAdvantage, diceEnabled, theme, characterRollContext } = this.props;

        const [{ messageTargetOptions, defaultMessageTargetOption, userId }] = this.context;

        return (
            <div
                className="ct-combat-mobile__extra ct-combat-mobile__extra--initiative"
                onClick={this.handleInitiativeClick}
            >
                {hasInitiativeAdvantage && (
                    <div className="ct-combat-mobile__advantage">
                        <AdvantageIcon
                            theme={theme}
                            className="ct-combat-mobile__advantage-icon"
                            title={'Advantage on Initiative'}
                        />
                    </div>
                )}
                <div className="ct-combat-mobile__extra-value">
                    <SignedNumberDice
                        number={initiative}
                        size={'large'}
                        diceNotation={DiceTools.CustomD20(initiative)}
                        rollType={RollType.Roll}
                        rollAction={'Initiative'}
                        diceEnabled={diceEnabled}
                        advMenu={true}
                        themeColor={theme.themeColor}
                        rollContext={characterRollContext}
                        rollTargetOptions={
                            messageTargetOptions ? Object.values(messageTargetOptions?.entities) : undefined
                        }
                        rollTargetDefault={defaultMessageTargetOption}
                        userId={userId}
                    />
                </div>
                <div className="ct-combat-mobile__extra-footer">
                    <div className="ct-combat-mobile__extra-label">Initiative</div>
                </div>
            </div>
        );
    };

    renderArmorClass = (): React.ReactNode => {
        const { armorClass } = this.props;

        return (
            <div className="ct-combat-mobile__extra  ct-combat-mobile__extra--ac" onClick={this.handleArmorClassClick}>
                <div className="ct-combat-mobile__extra-heading">
                    <div className="ct-combat-mobile__extra-label">Armor</div>
                </div>
                <div className="ct-combat-mobile__extra-value">{armorClass}</div>
                <div className="ct-combat-mobile__extra-footer">
                    <div className="ct-combat-mobile__extra-label">Class</div>
                </div>
            </div>
        );
    };

    render() {
        const { theme } = this.props;
        return (
            <div className={`ct-combat-mobile ${theme.isDarkMode ? 'ct-combat-mobile--dark-mode' : ''}`}>
                <div className="ct-combat-mobile__extras">
                    {this.renderProficiency()}
                    {this.renderSpeed()}
                    {this.renderInitiative()}
                    {this.renderArmorClass()}
                    <div className="ct-combat-mobile__extra ct-combat-mobile__extra--statuses">
                        <div className="ct-combat-mobile__ctas">
                            <div className="ct-combat-mobile__cta">
                                <div className="ct-combat-mobile__cta-button" onClick={this.handleDefensesSummaryClick}>
                                    Defenses
                                </div>
                            </div>
                            <div className="ct-combat-mobile__cta">
                                <div
                                    className="ct-combat-mobile__cta-button"
                                    onClick={this.handleConditionSummaryClick}
                                >
                                    Conditions
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

function mapStateToProps(state: SheetAppState) {
    return {
        initiative: rulesEngineSelectors.getProcessedInitiative(state),
        hasInitiativeAdvantage: rulesEngineSelectors.getHasInitiativeAdvantage(state),
        armorClass: rulesEngineSelectors.getAcTotal(state),
        proficiencyBonus: rulesEngineSelectors.getProficiencyBonus(state),
        speeds: rulesEngineSelectors.getCurrentCarriedWeightSpeed(state),
        preferences: rulesEngineSelectors.getCharacterPreferences(state),
        ruleData: rulesEngineSelectors.getRuleData(state),
        diceEnabled: appEnvSelectors.getDiceEnabled(state),
        theme: rulesEngineSelectors.getCharacterTheme(state),
        characterRollContext: characterRollContextSelectors.getCharacterRollContext(state),
    };
}

CombatMobile.contextType = GameLogContext;

export default connect(mapStateToProps)(CombatMobile);
